import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css'; // Make sure this is imported
import { Tables, Database } from '../types/supabase';

type Route = Tables<'route'>;
type Summit = Database['cq_static']['Tables']['summit']['Row'];

interface EventRouteMapProps {
  route: Route | null;
  summits: Summit[];
  eventName: string;
  onMapHover?: (position: { distance: number; elevation: number; coordinates: [number, number] } | null) => void;
  profileHoverPosition?: { distance: number; elevation: number; coordinates: [number, number] } | null;
}

// Set your Mapbox access token here
const MAPBOX_ACCESS_TOKEN = 'pk.eyJ1IjoiYWxhZmFuZWNoZXJlIiwiYSI6ImNsd3h1ZDQ4cjFlOGIybnIxN2J6aGVrYXgifQ._FHbFTWBayCyV6lziqciEA';

const EventRouteMap: React.FC<EventRouteMapProps> = ({ 
  route, 
  summits, 
  eventName, 
  onMapHover, 
  profileHoverPosition 
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    // Set access token
    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

    // Create map
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/alafanechere/cm87bvc6500a901sia74r2x15',
      center: [2.3522, 46.603354], // Center of France
      zoom: 10,
      attributionControl: true
    });

    // Add controls
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.current.addControl(new mapboxgl.FullscreenControl(), 'top-right');




    // Handle map load
    map.current.on('load', () => {
      setMapLoaded(true);
    });

    // Cleanup
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Add route and summits when map is loaded
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Clear existing sources and layers
    if (map.current.getSource('route')) {
      map.current.removeLayer('route-line');
      map.current.removeLayer('route-line-stroke');
      map.current.removeSource('route');
    }
    if (map.current.getSource('summits')) {
      map.current.removeLayer('summit-symbols');
      map.current.removeSource('summits');
    }
    if (map.current.getSource('tracker')) {
      map.current.removeLayer('tracker-symbol');
      map.current.removeSource('tracker');
    }
    if (map.current.getSource('start-marker')) {
      map.current.removeLayer('start-marker');
      map.current.removeLayer('start-marker-text');
      map.current.removeSource('start-marker');
    }
    if (map.current.getSource('finish-marker')) {
      map.current.removeLayer('finish-marker');
      map.current.removeLayer('finish-marker-text');
      map.current.removeSource('finish-marker');
    }

    // Add route if available
    if (route?.path) {
      try {
        const routePath = route.path as any;
        
        // Add route source
        map.current.addSource('route', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: routePath
          }
        });

        // Add route line with black stroke
        // First add the black stroke layer (thicker)
        map.current.addLayer({
          id: 'route-line-stroke',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#000000',
            'line-width': 12,
            'line-opacity': 0.8
          }
        });

        // Then add the colored line on top (thinner)
        map.current.addLayer({
          id: 'route-line',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#FF8C00',
            'line-width': 8,
            'line-opacity': 1
          }
        });

        // Add start and finish markers
        if (routePath.coordinates && routePath.coordinates.length > 0) {
          const startCoord = routePath.coordinates[0];
          const endCoord = routePath.coordinates[routePath.coordinates.length - 1];

          // Start marker source
          map.current.addSource('start-marker', {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [startCoord[0], startCoord[1]]
              },
              properties: {}
            }
          });

          // Finish marker source
          map.current.addSource('finish-marker', {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [endCoord[0], endCoord[1]]
              },
              properties: {}
            }
          });

          // Start marker layer
          map.current.addLayer({
            id: 'start-marker',
            type: 'circle',
            source: 'start-marker',
            paint: {
              'circle-radius': 10,
              'circle-color': '#62704f',
              'circle-stroke-color': '#000',
              'circle-stroke-width': 3
            }
          });

          // Start marker text
          map.current.addLayer({
            id: 'start-marker-text',
            type: 'symbol',
            source: 'start-marker',
            layout: {
              'text-field': 'START',
              'text-font': ['Jaro Regular', 'Arial Unicode MS Regular'],
              'text-size': 14,
              'text-anchor': 'top',
              'text-offset': [0, 1.5],
            },
            paint: {
              'text-color': '#000000',
              'text-halo-color': '#FFFFFF',
              'text-halo-width': 2
            }
          });

          // Finish marker layer
          map.current.addLayer({
            id: 'finish-marker',
            type: 'circle',
            source: 'finish-marker',
            paint: {
              'circle-radius': 10,
              'circle-color': '#FF8C00',
              'circle-stroke-color': '#000',
              'circle-stroke-width': 3
            }
          });

          // Finish marker text
          map.current.addLayer({
            id: 'finish-marker-text',
            type: 'symbol',
            source: 'finish-marker',
            layout: {
              'text-field': 'FINISH',
              'text-font': ['Jaro Regular', 'Arial Unicode MS Regular'],
              'text-size': 14,
              'text-anchor': 'top',
              'text-offset': [0, 1.5]
            },
            paint: {
              'text-color': '#000000',
              'text-halo-color': '#FFFFFF',
              'text-halo-width': 2
            }
          });

          // Fit map to route bounds
          const bounds = new mapboxgl.LngLatBounds();
          routePath.coordinates.forEach((coord: [number, number, number]) => {
            bounds.extend([coord[0], coord[1]]);
          });
          map.current.fitBounds(bounds, { padding: 100 });
        }
      } catch (error) {
        console.error('Error adding route:', error);
      }
    }

    // Add summits if available
    if (summits.length > 0) {
      const summitFeatures = summits.map(summit => ({
        type: 'Feature' as const,
        properties: {
          name: summit.name,
          elevation: `${Math.round(summit.elevation)} m`,
          type: summit.summit_type,
          bikeable: summit.bikeable,
          paved: summit.paved
        },
        geometry: {
          type: 'Point' as const,
          coordinates: (summit.location as any)?.coordinates || [0, 0]
        }
      }));

      // Add summits source
      map.current.addSource('summits', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: summitFeatures
        }
      });

      // Add summit symbols with icons and labels
      map.current.addLayer({
        id: 'summit-symbols',
        type: 'symbol',
        source: 'summits',
        layout: {
          'icon-image': 'triangle-filled',
          'text-field': ['to-string', ['get', 'name']],
          'text-font': ['Jaro Regular', 'Arial Unicode MS Regular'],
          'text-anchor': 'top',
          'text-offset': [0, 0.7]
        },
        paint: {
          'text-halo-width': 31.3,
          'text-halo-color': '#ff8c00',
          'text-color': '#290a0a'
        }
      });
    }
  }, [mapLoaded, route, summits]);

  // Handle profile hover position updates
  useEffect(() => {
    if (!map.current || !mapLoaded || !profileHoverPosition || !route?.path) return;

    try {
      const routePath = route.path as any;
      if (!routePath.coordinates || routePath.coordinates.length === 0) return;

      const routeCoords = routePath.coordinates as [number, number, number][];
      let targetCoordinates: [number, number] | null = null;
      let cumulativeDistance = 0;
      let previousPoint: [number, number, number] | null = null;

      // Find coordinates at specified distance
      for (let i = 0; i < routeCoords.length; i++) {
        const coord = routeCoords[i];
        
        if (previousPoint) {
          // Calculate distance between points using Haversine formula
          const [prevLng, prevLat] = previousPoint;
          const [lng, lat] = coord;
          const R = 6371000; // Earth's radius in meters
          const dLat = (lat - prevLat) * Math.PI / 180;
          const dLng = (lng - prevLng) * Math.PI / 180;
          const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                    Math.cos(prevLat * Math.PI / 180) * Math.cos(lat * Math.PI / 180) *
                    Math.sin(dLng / 2) * Math.sin(dLng / 2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          const segmentDistance = R * c;
          
          cumulativeDistance += segmentDistance;
          
          if (cumulativeDistance >= profileHoverPosition.distance) {
            const distanceRatio = (profileHoverPosition.distance - (cumulativeDistance - segmentDistance)) / segmentDistance;
            const interpolatedLng = prevLng + (lng - prevLng) * distanceRatio;
            const interpolatedLat = prevLat + (lat - prevLat) * distanceRatio;
            targetCoordinates = [interpolatedLng, interpolatedLat];
            break;
          }
        }
        previousPoint = coord;
      }

      if (!targetCoordinates && routeCoords.length > 0) {
        const lastCoord = routeCoords[routeCoords.length - 1];
        targetCoordinates = [lastCoord[0], lastCoord[1]];
      }

      if (targetCoordinates) {
        // Create or update tracker
        if (!map.current.getSource('tracker')) {
          map.current.addSource('tracker', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: [{
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: targetCoordinates
                },
                properties: {}
              }]
            }
          });

          map.current.addLayer({
            id: 'tracker-symbol',
            type: 'circle',
            source: 'tracker',
            paint: {
              'circle-radius': 8,
              'circle-color': '#FF0000',
              'circle-stroke-color': '#FFFFFF',
              'circle-stroke-width': 3
            }
          });
        } else {
          const source = map.current.getSource('tracker') as mapboxgl.GeoJSONSource;
          source.setData({
            type: 'FeatureCollection',
            features: [{
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: targetCoordinates
              },
              properties: {}
            }]
          });
        }
      }
    } catch (error) {
      console.error('Error updating tracker:', error);
    }
  }, [profileHoverPosition, mapLoaded, route]);

  // Clear tracker when no profile hover
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    if (!profileHoverPosition && map.current.getSource('tracker')) {
      if (map.current.getLayer('tracker-symbol')) {
        map.current.removeLayer('tracker-symbol');
      }
      if (map.current.getSource('tracker')) {
        map.current.removeSource('tracker');
      }
    }
  }, [profileHoverPosition, mapLoaded]);

  if (!route && summits.length === 0) {
    return (
      <div className="map-placeholder">
        <p>No route or summit data available to display on the map.</p>
      </div>
    );
  }

  return (
    <div className="event-route-map">
      <div ref={mapContainer} className="map-container event-route-map__container" />
    </div>
  );
};

export default EventRouteMap; 